import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { 
  FileText, Download, LogOut, RefreshCw, Lock, 
  FileSpreadsheet, IndianRupee, Building2, User, Phone
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ReportsPortal() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Auth state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);
  
  // Login state
  const [loginMethod, setLoginMethod] = useState('email'); // 'email' or 'otp'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [logging, setLogging] = useState(false);
  
  // Reports state
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [reportFromDate, setReportFromDate] = useState('');
  const [reportToDate, setReportToDate] = useState('');
  const [generatingReport, setGeneratingReport] = useState(false);

  // Check for existing session
  useEffect(() => {
    const savedToken = sessionStorage.getItem('reports_token');
    const savedUser = sessionStorage.getItem('reports_user');
    if (savedToken && savedUser) {
      setToken(savedToken);
      setUser(JSON.parse(savedUser));
      setIsAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && token) {
      fetchReportsData();
    }
  }, [isAuthenticated, token]);

  const handleEmailLogin = async () => {
    if (!email || !password) {
      toast.error('Please enter email and password');
      return;
    }

    // Only allow specific email
    if (email !== 'sujatha@assistivenetwork.com') {
      toast.error('Access denied. This portal is restricted.');
      return;
    }

    setLogging(true);
    try {
      const response = await fetch(`${API}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      if (response.ok) {
        const data = await response.json();
        
        // Verify user has reports role
        if (!data.user?.roles?.includes('reports') && data.user?.role !== 'reports') {
          toast.error('Access denied. Reports access required.');
          return;
        }
        
        setToken(data.access_token);
        setUser(data.user);
        setIsAuthenticated(true);
        sessionStorage.setItem('reports_token', data.access_token);
        sessionStorage.setItem('reports_user', JSON.stringify(data.user));
        toast.success('Login successful');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Login failed');
      }
    } catch (error) {
      toast.error('Login failed');
    } finally {
      setLogging(false);
    }
  };

  const sendOTP = async () => {
    if (!phone) {
      toast.error('Please enter phone number');
      return;
    }

    // Only allow specific phone
    const cleanPhone = phone.replace(/\D/g, '').slice(-10);
    if (cleanPhone !== '9900893624') {
      toast.error('Access denied. This portal is restricted.');
      return;
    }

    setLogging(true);
    try {
      const response = await fetch(`${API}/api/auth/send-employee-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: cleanPhone })
      });

      if (response.ok) {
        setOtpSent(true);
        toast.success('OTP sent to your WhatsApp');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send OTP');
      }
    } catch (error) {
      toast.error('Failed to send OTP');
    } finally {
      setLogging(false);
    }
  };

  const verifyOTP = async () => {
    if (!otp) {
      toast.error('Please enter OTP');
      return;
    }

    const cleanPhone = phone.replace(/\D/g, '').slice(-10);
    
    setLogging(true);
    try {
      const response = await fetch(`${API}/api/auth/verify-employee-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: cleanPhone, otp })
      });

      if (response.ok) {
        const data = await response.json();
        setToken(data.access_token);
        setUser(data.user);
        setIsAuthenticated(true);
        sessionStorage.setItem('reports_token', data.access_token);
        sessionStorage.setItem('reports_user', JSON.stringify(data.user));
        toast.success('Login successful');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'OTP verification failed');
      }
    } catch (error) {
      toast.error('OTP verification failed');
    } finally {
      setLogging(false);
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    sessionStorage.removeItem('reports_token');
    sessionStorage.removeItem('reports_user');
    setOtpSent(false);
    setOtp('');
    toast.success('Logged out');
  };

  const fetchReportsData = async () => {
    setLoading(true);
    try {
      // Fetch stats
      const statsRes = await fetch(`${API}/api/gst/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (statsRes.ok) {
        setStats(await statsRes.json());
      }

      // Fetch recent invoices
      const invRes = await fetch(`${API}/api/gst/invoices?limit=50`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (invRes.ok) {
        const data = await invRes.json();
        setInvoices(data.invoices || []);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadReport = async (type) => {
    if (!reportFromDate || !reportToDate) {
      toast.error('Please select date range');
      return;
    }

    setGeneratingReport(true);
    try {
      const response = await fetch(
        `${API}/api/gst/reports/export/${type}?from_date=${reportFromDate}&to_date=${reportToDate}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `GST_${type.toUpperCase()}_${reportFromDate}_to_${reportToDate}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success('Report downloaded');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to generate report');
      }
    } catch (error) {
      toast.error('Failed to download report');
    } finally {
      setGeneratingReport(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <Toaster richColors position="top-right" />
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
              <Lock className="h-8 w-8 text-blue-600" />
            </div>
            <CardTitle className="text-2xl">GST Reports Portal</CardTitle>
            <CardDescription>Restricted access - Authorized users only</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={loginMethod} onValueChange={setLoginMethod}>
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="email">Email Login</TabsTrigger>
                <TabsTrigger value="otp">WhatsApp OTP</TabsTrigger>
              </TabsList>

              <TabsContent value="email" className="space-y-4">
                <div>
                  <Label>Email Address</Label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="sujatha@assistivenetwork.com"
                    data-testid="reports-email"
                  />
                </div>
                <div>
                  <Label>Password</Label>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter password"
                    data-testid="reports-password"
                    onKeyDown={(e) => e.key === 'Enter' && handleEmailLogin()}
                  />
                </div>
                <Button 
                  className="w-full" 
                  onClick={handleEmailLogin} 
                  disabled={logging}
                  data-testid="reports-login-btn"
                >
                  {logging ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}
                  Sign In
                </Button>
              </TabsContent>

              <TabsContent value="otp" className="space-y-4">
                <div>
                  <Label>Mobile Number</Label>
                  <Input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="9900893624"
                    disabled={otpSent}
                    data-testid="reports-phone"
                  />
                </div>
                {otpSent ? (
                  <>
                    <div>
                      <Label>Enter OTP</Label>
                      <Input
                        type="text"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value)}
                        placeholder="6-digit OTP"
                        maxLength={6}
                        data-testid="reports-otp"
                        onKeyDown={(e) => e.key === 'Enter' && verifyOTP()}
                      />
                    </div>
                    <Button 
                      className="w-full" 
                      onClick={verifyOTP} 
                      disabled={logging}
                      data-testid="reports-verify-btn"
                    >
                      {logging ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}
                      Verify OTP
                    </Button>
                    <Button 
                      variant="outline" 
                      className="w-full" 
                      onClick={() => { setOtpSent(false); setOtp(''); }}
                    >
                      Change Number
                    </Button>
                  </>
                ) : (
                  <Button 
                    className="w-full" 
                    onClick={sendOTP} 
                    disabled={logging}
                    data-testid="reports-send-otp-btn"
                  >
                    {logging ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Phone className="h-4 w-4 mr-2" />}
                    Send OTP via WhatsApp
                  </Button>
                )}
              </TabsContent>
            </Tabs>

            <p className="text-xs text-center text-slate-500 mt-6">
              This portal is restricted to authorized personnel only.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Reports Dashboard
  return (
    <div className="min-h-screen bg-slate-100">
      <Toaster richColors position="top-right" />
      
      {/* Header */}
      <header className="bg-slate-900 text-white py-4 px-6 shadow-lg">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
              <FileSpreadsheet className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-xl font-bold">GST Reports Portal</h1>
              <p className="text-sm text-slate-400">{user?.full_name || user?.email}</p>
            </div>
          </div>
          <Button variant="outline" onClick={logout} className="border-slate-600 text-slate-300 hover:bg-slate-800">
            <LogOut className="h-4 w-4 mr-2" />
            Logout
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto p-6 space-y-6">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : (
          <>
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>Total Invoices</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold">{stats?.total_invoices || 0}</p>
                  <p className="text-xs text-slate-500">{stats?.period}</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardDescription className="flex items-center gap-1">
                    <Building2 className="h-4 w-4" /> B2B
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold text-blue-600">{stats?.b2b_count || 0}</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardDescription className="flex items-center gap-1">
                    <User className="h-4 w-4" /> B2C
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold text-green-600">{stats?.b2c_count || 0}</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>Total Revenue</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold text-emerald-600">{formatCurrency(stats?.total_revenue)}</p>
                </CardContent>
              </Card>
            </div>

            {/* Generate Reports */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileSpreadsheet className="h-5 w-5" />
                  Generate GST Reports
                </CardTitle>
                <CardDescription>Download GSTR-1 format reports for filing</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap items-end gap-4">
                  <div>
                    <Label>From Date</Label>
                    <Input
                      type="date"
                      value={reportFromDate}
                      onChange={(e) => setReportFromDate(e.target.value)}
                      className="w-40"
                      data-testid="report-from"
                    />
                  </div>
                  <div>
                    <Label>To Date</Label>
                    <Input
                      type="date"
                      value={reportToDate}
                      onChange={(e) => setReportToDate(e.target.value)}
                      className="w-40"
                      data-testid="report-to"
                    />
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => downloadReport('b2b')}
                    disabled={generatingReport}
                    data-testid="download-b2b"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    B2B Report
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => downloadReport('b2c')}
                    disabled={generatingReport}
                    data-testid="download-b2c"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    B2C Report
                  </Button>
                  <Button
                    onClick={() => downloadReport('all')}
                    disabled={generatingReport}
                    data-testid="download-all"
                  >
                    {generatingReport ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4 mr-2" />
                    )}
                    All Invoices
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Recent Invoices */}
            <Card>
              <CardHeader>
                <CardTitle>Recent Invoices</CardTitle>
                <CardDescription>Last 50 invoices</CardDescription>
              </CardHeader>
              <CardContent>
                {invoices.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No invoices found</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Invoice #</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>Customer</TableHead>
                          <TableHead>GSTIN</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead className="text-right">Taxable</TableHead>
                          <TableHead className="text-right">GST</TableHead>
                          <TableHead className="text-right">Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {invoices.map((inv) => (
                          <TableRow key={inv.invoice_id}>
                            <TableCell className="font-medium">{inv.invoice_number}</TableCell>
                            <TableCell>{inv.invoice_date}</TableCell>
                            <TableCell>{inv.customer_name}</TableCell>
                            <TableCell className="text-xs text-slate-500">{inv.customer_gstin || '-'}</TableCell>
                            <TableCell>
                              <Badge variant={inv.invoice_type === 'B2B' ? 'default' : 'secondary'}>
                                {inv.invoice_type}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">{formatCurrency(inv.taxable_value)}</TableCell>
                            <TableCell className="text-right">{formatCurrency(inv.total_gst)}</TableCell>
                            <TableCell className="text-right font-medium">{formatCurrency(inv.total_amount)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </main>
    </div>
  );
}
